import {
  ChipSwitch,
  Grid,
  InfoTooltip,
  StatusTooltip,
  Switch,
  Typography,
} from 'app/design';
import { useFindInPage } from 'app/components/FindInPage';
import { GenericSettingsSwitchTemplate } from 'app/components/GenericSettingsSwitchTemplate';
import { useUpdateVmboxPartial } from 'app/hooks/mutations/vmbox';
import * as React from 'react';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { Vmbox, VmboxDoc } from 'types/vmbox';

interface AdditionalSettingProps {
  vmbox: Vmbox;
}

interface FormValues {
  announcement_only: boolean;
}

const AnnouncementOnly = ({ vmbox }: AdditionalSettingProps) => {
  const { ref, HighlightPulse, highlightPulseProps } = useFindInPage({
    name: 'announcementOnly',
    options: [
      {
        label: 'Announcement Only',
        value: 'announcementOnly',
      },
    ],
    onChoose: 'matchAnyScrollToRef',
  });
  const [showTooltip, setShowTooltip] = useState(false);
  const updateVmbox = useUpdateVmboxPartial();
  const {
    formState: { isDirty },
    watch,
    control,
    handleSubmit,
    reset,
  } = useForm<FormValues>({
    defaultValues: { announcement_only: vmbox.doc.announcement_only ?? false },
  });
  const watchEnabled = watch('announcement_only');
  const statusRef = useRef(updateVmbox.status);
  statusRef.current = updateVmbox.status;

  // reset form if vmbox doc changes
  useEffect(() => {
    reset({ announcement_only: vmbox.doc.announcement_only ?? false });
  }, [vmbox]);

  // display switch tooltip based on status
  useEffect(() => {
    if (updateVmbox.status === 'loading') setShowTooltip(true);
    else if (showTooltip)
      setTimeout(
        // @ts-ignore
        () => statusRef.current !== 'loading' && setShowTooltip(false),
        1000,
      );
  }, [updateVmbox.status]);

  // trigger submit on switch trigger
  useEffect(() => {
    if (isDirty) {
      // only submit if form is dirty (we reset form on success, fail)
      handleSubmit(onSubmit)();
    }
  }, [watchEnabled]);

  const onSubmit = async (formValues: FormValues) => {
    await updateVmbox.mutateAsync(
      {
        id: vmbox.id,
        ...formValues,
      },
      {
        onSuccess: resp => {
          const { data }: { data: VmboxDoc } = resp;
          console.log('data', data);
          // reset form data (needed since this form is initialized once, not
          // - every time like when a dialog opens). We need to reset the default
          // - values which are the source of truth for error reset/isDirty value
          reset({ announcement_only: data.announcement_only ?? false });
        },
        onError: () => {
          reset(); // reset form to last valid state
          toast.error('Failed to update vmbox settings.');
        },
      },
    );
  };

  return (
    // @ts-ignore
    <div ref={ref}>
      <HighlightPulse {...highlightPulseProps} />
      <GenericSettingsSwitchTemplate
        title={'Announcement Only'}
        caption={
          'Determine if the mailbox should only play announcements (do not allow voicemails to be recorded)'
        }
      >
        <Controller
          control={control}
          name={'announcement_only'}
          render={({ field: { onChange, value, ...props } }) => (
            <StatusTooltip
              status={updateVmbox.status}
              placement={'right'}
              open={showTooltip}
            >
              <Switch
                size="small"
                checked={value}
                onChange={e => onChange(e.target.checked)}
              />
            </StatusTooltip>
          )}
        />
      </GenericSettingsSwitchTemplate>
    </div>
  );
};

export default AnnouncementOnly;
